import { useEffect, useMemo, useState } from "react"
import { useIntl } from "react-intl"
import { useHistory } from "react-router-dom"
import { FormOnSubmitHandler } from "@carrotfertility/carotene-core-x"
import useSubmitProviderRequest from "#/pages/provider-finder/hooks/useSubmitProviderRequest"
import { RequestedProviderType, GenderIdentity } from "#/pages/provider-finder/types/ProviderRequest"
import useShowProviderRecommendations from "../../hooks/useShowProviderRecommendations"
import { ProviderFinderParam, providerFinderRootPath } from "../../utils/providerFinderSearchUrl"
import { useMemberActionTracking } from "#/services/memberActionTracking/useMemberActionTracking"
import { MemberActions } from "#/services/memberActionTracking/memberActions"
import { useGetLocationDetails } from "#/pages/provider-finder/hooks/useGetLocationDetails"
import { usePageViewTracking } from "#/services/analytics/usePageViewTracking"
import { ProviderRequested } from "#/pages/provider-finder"
import { useCurrentUser } from "#/components/context/user/UserContext"
import useGetBenefit from "#/components/dynamic-content/hooks/useGetBenefit"
import { getProviderVariantContent, buildLanguageOptions, ProviderVariantContent } from "./providerVariantContent"

export type HealthInsuranceAutocompleteOption = {
  label: string
  value: string
}

export type GarnerHealthProviderRequestFormValues = {
  providerType: RequestedProviderType
  healthInsurance: HealthInsuranceAutocompleteOption
  language: string
  location: string
  genderIdentity: GenderIdentity
  genderIdentityOther: string
  additionalDetails: string
}

const RTX_COMPANY_IDS = [
  5281, 18054, 18027, 18243, 19525, 19527, 19526, 19528, 18058, 18244, 19529, 19564, 19563, 19565, 19530, 19531, 19533,
  19532, 18059, 18245, 18057, 18061, 18247, 18060, 18246, 18056, 18055, 19428, 19429, 18053, 18248, 18064
]

const BLUE_CARD_PPO = "Blue Card PPO"

export interface UseProviderRecommendationFormResult {
  isLoading: boolean
  shouldRedirect: boolean
  redirectPath: string
  isError: boolean
  isSuccess: boolean
  variantContent: ProviderVariantContent
  languageOptions: { label: string; value: string }[]
  showTelemedicineSection: boolean
  defaultLocation: google.maps.places.AutocompletePrediction | null
  defaultLocationString: string | null
  defaultHealthInsurance: HealthInsuranceAutocompleteOption | undefined
  setLocationPickerStringValue: (v: string) => void
  onSubmitHandler: FormOnSubmitHandler<GarnerHealthProviderRequestFormValues>
  onClose: () => void
}

export function useProviderRecommendationForm(
  providerRequested: RequestedProviderType
): UseProviderRecommendationFormResult {
  const intl = useIntl()
  const history = useHistory()
  const currentUser = useCurrentUser()
  const { mutateAsync, isError, isSuccess } = useSubmitProviderRequest()
  const { data: providerRecommendationsConfig, isLoading: isProviderRecommendationsLoading } =
    useShowProviderRecommendations()
  const benefitConfiguration = useGetBenefit()

  const searchParams = new URLSearchParams(history.location.search)
  const defaultLocationString = searchParams.get("location")
  const defaultLocation: google.maps.places.AutocompletePrediction = defaultLocationString
    ? {
        description: defaultLocationString,
        matched_substrings: [],
        place_id: defaultLocationString,
        structured_formatting: {
          main_text: "defaultLocationString",
          main_text_matched_substrings: [],
          secondary_text: ""
        },
        terms: [],
        types: []
      }
    : null

  usePageViewTracking()

  const [locationPickerStringValue, setLocationPickerStringValue] = useState(defaultLocationString ?? "")
  const locationDetails = useGetLocationDetails(locationPickerStringValue)

  const params = useMemo(() => new URLSearchParams(history.location.search), [history.location.search])
  const { mutate: trackMemberAction } = useMemberActionTracking()

  useEffect(() => {
    trackMemberAction({
      memberActionTaken: MemberActions.PROVIDER_FINDER_RECOMMENDATION_FORM_VIEWED,
      metaData: {
        providerType: providerRequested,
        latitude: params.get(ProviderFinderParam.Latitude)
          ? Number(params.get(ProviderFinderParam.Latitude))
          : undefined,
        longitude: params.get(ProviderFinderParam.Longitude)
          ? Number(params.get(ProviderFinderParam.Longitude))
          : undefined,
        source:
          params.get(ProviderFinderParam.Latitude) && params.get(ProviderFinderParam.Longitude)
            ? "providerFinderTypeSearch"
            : "other"
      }
    })
  }, [params, providerRequested, trackMemberAction])

  const isLoading = isProviderRecommendationsLoading || benefitConfiguration.isLoading
  const hasObGynFertilityCoverage =
    benefitConfiguration.data?.journeys?.fertilityCare?.eligibleExpensesDetail?.obGynProvidedFertilityCareCovered ??
    false
  const variantContent = getProviderVariantContent(intl, hasObGynFertilityCoverage)[providerRequested]
  const shouldRedirect = !isLoading && !providerRecommendationsConfig?.[variantContent.configFlag]

  const onSubmitHandler: FormOnSubmitHandler<GarnerHealthProviderRequestFormValues> = async (formFields) => {
    const requestBody = {
      providerType: providerRequested,
      healthInsurance: formFields.healthInsurance.value,
      location: locationPickerStringValue,
      language: formFields.language,
      genderIdentity: formFields.genderIdentity,
      genderIdentityOther: formFields.genderIdentityOther,
      additionalDetails: formFields.additionalDetails
    }
    try {
      if (!locationDetails.isLoading) {
        trackMemberAction({
          memberActionTaken: MemberActions.PROVIDER_FINDER_RECOMMENDATION_FORM_SUBMITTED,
          metaData: {
            providerType: providerRequested,
            latitude: locationDetails.data?.latitude ?? Number(params.get(ProviderFinderParam.Latitude)),
            longitude: locationDetails.data?.longitude ?? Number(params.get(ProviderFinderParam.Longitude))
          }
        })
      }
      await mutateAsync(requestBody)
    } catch {
      // https://github.com/react-hook-form/react-hook-form/issues/9821
      // ignored - we use the isError state from useSubmitProviderRequest() to handle errors
    }
  }

  const onClose = () => {
    history.push("/")
  }

  const languageOptions = buildLanguageOptions(intl)

  const isProduction = process.env.NODE_ENV === "production"
  const companyId = currentUser?.company?.companyId
  const shouldDefaultToBlueCardPPO = isProduction && companyId && RTX_COMPANY_IDS.includes(companyId)
  const defaultHealthInsurance = shouldDefaultToBlueCardPPO ? { label: BLUE_CARD_PPO, value: BLUE_CARD_PPO } : undefined
  const blueberryPediatricsEnabled = benefitConfiguration.data?.telemedicine?.providers?.blueberryPediatricsEnabled
  const showTelemedicineSection = providerRequested === ProviderRequested.Pediatrician && blueberryPediatricsEnabled

  return {
    isLoading,
    shouldRedirect,
    redirectPath: providerFinderRootPath,
    isError,
    isSuccess,
    variantContent,
    languageOptions,
    showTelemedicineSection,
    defaultLocation,
    defaultLocationString,
    defaultHealthInsurance,
    setLocationPickerStringValue,
    onSubmitHandler,
    onClose
  }
}
